import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translateText } from '../services/translationService';

const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { language } = useLanguage();
  const [translatedSlides, setTranslatedSlides] = useState([]);
  const [translating, setTranslating] = useState(false);
  
  const originalSlides = [
    {
      id: 1,
      image: '/images/hero-1.jpg',
      title: 'D4U Naturally',
      subtitle: 'Discover the authentic beauty of Moroccan argan oil',
      description: 'Pure, natural, and crafted with centuries of tradition.',
      button1Text: 'Shop Collection',
      button1Link: '/shop',
      button2Text: 'Our Story',
      button2Link: '/our-universe'
    },
    {
      id: 2,
      image: '/images/hero-2.jpg',
      title: 'Premium Quality',
      subtitle: '100% Organic Argan Oil',
      description: 'Harvested from the finest argan trees in Morocco',
      button1Text: 'View Products',
      button1Link: '/shop',
      button2Text: 'Learn More',
      button2Link: '/our-universe'
    },
    {
      id: 3,
      image: '/images/hero-3.jpg',
      title: 'Natural Beauty',
      subtitle: 'For Radiant Skin & Hair',
      description: 'Transform your beauty routine with nature\'s miracle',
      button1Text: 'Discover',
      button1Link: '/shop',
      button2Text: 'Our Story',
      button2Link: '/our-universe'
    },
    {
      id: 4,
      image: '/images/hero-4.jpg',
      title: 'Moroccan Heritage',
      subtitle: 'Traditional Craftsmanship',
      description: 'Preserving ancient beauty secrets for modern wellness',
      button1Text: 'Shop Now',
      button1Link: '/shop',
      button2Text: 'Heritage',
      button2Link: '/our-universe'
    },
    {
      id: 5,
      image: '/images/hero-5.jpg',
      title: 'Pure Essence',
      subtitle: 'Nature\'s Finest Ingredients',
      description: 'Experience the power of pure, unadulterated natural extracts',
      button1Text: 'Explore',
      button1Link: '/shop',
      button2Text: 'Ingredients',
      button2Link: '/our-universe'
    },
    {
      id: 6,
      image: '/images/hero-6.jpg',
      title: 'Luxury Care',
      subtitle: 'Elevate Your Daily Routine',
      description: 'Premium skincare and haircare for the discerning individual',
      button1Text: 'Shop Luxury',
      button1Link: '/shop',
      button2Text: 'Collections',
      button2Link: '/shop'
    },
    {
      id: 7,
      image: '/images/hero-7.jpg',
      title: 'Sustainable Beauty',
      subtitle: 'Eco-Friendly & Ethical',
      description: 'Beauty that cares for you and the planet',
      button1Text: 'Learn More',
      button1Link: '/sustainability',
      button2Text: 'Our Mission',
      button2Link: '/our-universe'
    },
    {
      id: 8,
      image: '/images/hero-8.jpg',
      title: 'Expert Formulations',
      subtitle: 'Science Meets Nature',
      description: 'Advanced formulas backed by scientific research',
      button1Text: 'View Science',
      button1Link: '/our-universe',
      button2Text: 'Shop Now',
      button2Link: '/shop'
    }
  ];

  // Traduction des slides
  useEffect(() => {
    const translateSlides = async () => {
      if (language === 'en') {
        setTranslatedSlides(originalSlides);
        return;
      }

      try {
        setTranslating(true);
        console.log(`🌐 Traduction HeroCarousel: en → ${language}`);

        const translated = await Promise.all(
          originalSlides.map(async (slide) => {
            try {
              const [
                translatedSubtitle,
                translatedDescription,
                translatedButton1,
                translatedButton2
              ] = await Promise.all([
                translateText(slide.subtitle, language, 'en'),
                translateText(slide.description, language, 'en'),
                translateText(slide.button1Text, language, 'en'),
                translateText(slide.button2Text, language, 'en')
              ]);

              return {
                ...slide,
                subtitle: translatedSubtitle,
                description: translatedDescription,
                button1Text: translatedButton1,
                button2Text: translatedButton2
              };
            } catch (error) {
              console.error(`Erreur traduction slide ${slide.id}:`, error);
              return slide; // Retourner l'original en cas d'erreur
            }
          })
        );

        setTranslatedSlides(translated);
        console.log('✅ HeroCarousel traduit avec succès');
      } catch (error) {
        console.error('💥 Erreur générale traduction HeroCarousel:', error);
        setTranslatedSlides(originalSlides);
      } finally {
        setTranslating(false);
      }
    };

    translateSlides();
  }, [language]);

  // Utiliser les slides traduits ou les originaux
  const slides = translatedSlides.length > 0 ? translatedSlides : originalSlides;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const goToPrev = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Touch support for mobile
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      goToNext();
    }

    if (touchStart - touchEnd < -50) {
      goToPrev();
    }
  };

  // Texte pour les boutons de navigation selon la langue
  const getNavigationText = () => {
    if (language === 'fr') return { prev: 'Précédent', next: 'Suivant' };
    if (language === 'ar') return { prev: 'السابق', next: 'التالي' };
    return { prev: 'Previous', next: 'Next' };
  };

  const navText = getNavigationText();

  return (
    <div 
      className="relative h-screen overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Overlay de traduction */}
      {translating && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-30">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#d0b125]"></div>
            <p className="text-[#2f2a1e] font-light">
              {language === 'fr' ? 'Mise à jour des traductions...' : 
               language === 'ar' ? 'جاري تحديث الترجمات...' : 
               'Updating translations...'}
            </p>
          </div>
        </div>
      )}

      {/* Slides */}
      <div className="relative h-full">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-all duration-700 ease-in-out ${
              index === currentSlide
                ? 'opacity-100 translate-x-0'
                : index < currentSlide
                ? 'opacity-0 -translate-x-full'
                : 'opacity-0 translate-x-full'
            }`}
          >
            {/* Background Image with Light Overlay */}
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              <div className="absolute inset-0 bg-black bg-opacity-40"></div>
            </div>
            
            {/* Content */}
            <div className="relative z-10 flex items-center justify-center h-full">
              <div className="text-center text-white px-6 max-w-4xl">
                {/* Titre principal - Élégant et professionnel */}
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-normal mb-6 tracking-wide">
                  {slide.title}
                </h1>

                {/* Sous-titre - Raffiné */}
                <h2 className="text-xl md:text-2xl lg:text-3xl font-light mb-6 tracking-wider uppercase letter-spacing-2">
                  {slide.subtitle}
                </h2>

                {/* Description - Clair et lisible */}
                <p className="text-lg md:text-xl mb-10 font-light leading-relaxed max-w-2xl mx-auto">
                  {slide.description}
                </p>

                {/* Boutons - Professionnels */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link 
                    to={slide.button1Link}
                    className="bg-[#d0b125] text-white px-8 py-4 rounded-sm hover:bg-[#b89a20] transition-colors duration-300 text-base font-medium min-w-[180px]"
                  >
                    {slide.button1Text}
                  </Link>
                  
                  <Link 
                    to={slide.button2Link}
                    className="bg-transparent text-white border border-white px-8 py-4 rounded-sm hover:bg-white hover:text-[#2f2a1e] transition-colors duration-300 text-base font-medium min-w-[180px]"
                  >
                    {slide.button2Text}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows - Discrètes et élégantes */}
      <button
        onClick={goToPrev}
        className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 text-white p-3 rounded-sm hover:bg-opacity-30 transition-colors duration-300 z-20 backdrop-blur-sm"
        aria-label={navText.prev}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 19l-7-7 7-7"/>
        </svg>
      </button>
      
      <button
        onClick={goToNext}
        className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 text-white p-3 rounded-sm hover:bg-opacity-30 transition-colors duration-300 z-20 backdrop-blur-sm"
        aria-label={navText.next}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5l7 7-7 7"/>
        </svg>
      </button>

      {/* Indicators - Discrets */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 rounded-full transition-colors duration-300 ${
              index === currentSlide
                ? 'bg-[#d0b125]'
                : 'bg-white bg-opacity-50 hover:bg-opacity-75'
            }`}
            aria-label={`${language === 'fr' ? 'Aller à la slide' : 
                        language === 'ar' ? 'انتقل إلى الشريحة' : 
                        'Go to slide'} ${index + 1}`}
          />
        ))}
      </div>

      {/* Progress Bar - Subtile */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-white bg-opacity-20 z-20">
        <div 
          className="h-full bg-[#d0b125] transition-all duration-5000 ease-linear"
          style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
        />
      </div>

      {/* Numéro de slide actuel - Élégant */}
      

      {/* Indicateur de langue en debug */}
      
    </div>
  );
};

export default HeroCarousel;